import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ConvertStatusById } from "@/utils/convert-status-to-name/convert-status-to-name";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useBooking } from "../../../hooks/use-booking";
import {
  SheetEditBookingFormData,
  SheetEditBookingProps,
} from "./sheet-edit-booking.types";
import { SheetEditBookingSchema } from "./sheet-edit-booking.validation";
import { Check } from "lucide-react";
import { createToast } from "vercel-toast";

export const SheetEditBooking = ({
  children,
  data,
  updateFilters,
  filters,
}: SheetEditBookingProps) => {
  const { handleUpdatePoBooking } = useBooking();
  const [sheetOpen, setSheetOpen] = useState<boolean>();
  const [hiddeSubmitButton, setHiddeSubmitButton] = useState<boolean>(false);

  const getBookingDate = (key: number) => {
    const arrDates = data.booking_detail.split(" - ");
    return arrDates[key];
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isSubmitting },
  } = useForm<SheetEditBookingFormData>({
    resolver: yupResolver(SheetEditBookingSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      take: Number(data.bookingAttributes.booking_payout_rate_perc),
      credits: data.credits,
      transfer: Number(data.bookingAttributes.booking_payout),
    },
  });

  useEffect(() => {
    reset();
  }, [sheetOpen, reset]);

  const onSubmit = async (formData: SheetEditBookingFormData) => {
    setHiddeSubmitButton(true);

    if (!data.po_contract_id) {
      createToast("Erro ao atualizar booking, contrato não encontrado", {
        type: "error",
        timeout: 2000,
      });
      setSheetOpen(false);
      setHiddeSubmitButton(false);
      updateFilters(filters);
      return;
    }

    try {
      await handleUpdatePoBooking({
        bookingId: data.id,
        contractId: data.po_contract_id,
        take: Number(formData.take),
        credits: formData.credits,
        transfer: formData.transfer,
      });
      setSheetOpen(false);
      updateFilters(filters);
    } catch {
      createToast("Erro ao atualizar booking. Tente novamente.", {
        type: "error",
        timeout: 3000,
      });
    } finally {
      setHiddeSubmitButton(false);
    }
  };

  const statusTitle = ConvertStatusById({
    status: data.status,
    type: "STATUS_BOOKINGS",
  }).title;

  return (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent className="w-screen " side={"bottom"}>
        <div className="p-4 max-w-4xl mx-auto">
          <div className="max-w-4xl mx-auto mt-2 mb-4">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-2 gap-x-4 gap-y-6">
                <div className="flex flex-col">
                  <label className="font-medium text-sm" htmlFor="company">
                    Empresa
                  </label>
                  <div className="bg-white py-1 px-2 text-sm text-gray-500 mt-1 border border-gray-100 min-h-[35.99px] rounded flex items-center shadow-sm">
                    {data.companies.name}
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="font-medium text-sm" htmlFor="space-name">
                    Nome do espaço
                  </label>
                  <Input
                    id="space-name"
                    value={data.spaces.name}
                    disabled
                    readOnly
                  />
                </div>

                <div className="flex flex-col">
                  <label className="font-medium text-sm" htmlFor="room-name">
                    Escritório
                  </label>
                  <div className="bg-white py-1 px-2 text-sm text-gray-500 mt-1 border border-gray-100 min-h-[35.99px] rounded flex items-center shadow-sm">
                    {data?.privateRooms?.label}
                  </div>
                </div>

                <div className="flex flex-col text-sm">
                  <label className="font-medium text-sm" htmlFor="status">
                    Status
                  </label>
                  <Input id="status" value={statusTitle} disabled readOnly />
                </div>

                <div className="flex flex-col">
                  <label className="font-medium text-sm" htmlFor="start-date">
                    Data de início
                  </label>
                  <Input
                    id="start-date"
                    type="date"
                    value={getBookingDate(0)}
                    disabled
                    readOnly
                  />
                </div>

                <div className="flex flex-col">
                  <label className="font-medium text-sm" htmlFor="end-date">
                    Data de término
                  </label>
                  <Input
                    id="end-date"
                    type="date"
                    value={getBookingDate(1)}
                    disabled
                    readOnly
                  />
                </div>
              </div>
              <div className="w-full self-stretch bg-border border mt-6" />

              <h2 className="text-lg font-semibold mt-6">Campos editáveis</h2>

              <div className="grid grid-cols-3 mt-6 gap-4">
                <Controller
                  name="take"
                  control={control}
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col">
                      <label className="font-medium text-sm" htmlFor="take">
                        Take %
                      </label>
                      <Input
                        {...field}
                        id="take"
                        type="number"
                        placeholder="Take"
                        onChange={(event) => {
                          field.onChange(event);
                        }}
                        error={fieldState.error ? fieldState.error.message : ""}
                      />
                    </div>
                  )}
                />
                <Controller
                  name="credits"
                  control={control}
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col">
                      <label className="font-medium text-sm" htmlFor="credits">
                        Créditos
                      </label>
                      <Input
                        {...field}
                        id="credits"
                        type="number"
                        placeholder="Credits"
                        onChange={(event) => {
                          field.onChange(event);
                        }}
                        error={fieldState.error ? fieldState.error.message : ""}
                      />
                    </div>
                  )}
                />
                <Controller
                  name="transfer"
                  control={control}
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col">
                      <label className="font-medium text-sm" htmlFor="transfer">
                        Repasse
                      </label>
                      <Input
                        {...field}
                        id="transfer"
                        placeholder="Transfer"
                        type="number"
                        onChange={(event) => {
                          field.onChange(event);
                        }}
                        error={fieldState.error ? fieldState.error.message : ""}
                      />
                    </div>
                  )}
                />
              </div>
              <div className="w-full self-stretch bg-border border mt-6" />
              <div className="mt-6 flex justify-end space-x-4">
                <SheetClose>
                  <Button
                    type="button"
                    className="bg-white border border-gray-200 text-black shadow-none hover:bg-gray-200"
                  >
                    ← Voltar aos bookings
                  </Button>
                </SheetClose>{" "}
                <Button
                  className="text-white shadow-none"
                  type="submit"
                  disabled={!isValid || isSubmitting || hiddeSubmitButton}
                >
                  <Check className="w-4 h-4 mr-2" /> Salvar edição
                </Button>
              </div>
            </form>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
